import { useLocation } from "react-router";
import s from "./BulkCreateCompanyWizardStepper.module.css";

/**
 * Three visible steps — Validate/Commit are actions inside Review, not steps
 * of their own. The active step is derived from the route in
 * `useActiveStepIndex`, so the labels are all this list needs to carry.
 */
const STEPS = [
  { label: "Upload" },
  { label: "Review" },
  { label: "Results" },
] as const;

const BASE_PATH = "/onboarding/bulk-create-company";

/** Derive the 0-based index of the active step from the current pathname. */
function useActiveStepIndex(): number {
  const { pathname } = useLocation();
  const suffix = pathname.replace(BASE_PATH, "");

  if (suffix === "/review") return 1;
  if (suffix === "/results") return 2;
  return 0;
}

type StepStatus = "pending" | "active" | "completed";

/**
 * Read-only wizard step indicator used in `BulkCreateCompanyLayout`. Shows
 * three steps (Upload → Review → Results) with the active step derived from
 * the current route.
 */
export function BulkCreateCompanyWizardStepper() {
  const activeIndex = useActiveStepIndex();

  return (
    <nav aria-label="Bulk create companies progress">
      <ol className={s.stepper}>
        {STEPS.map((step, index) => {
          const status: StepStatus =
            index < activeIndex
              ? "completed"
              : index === activeIndex
                ? "active"
                : "pending";

          return (
            <li
              key={step.label}
              className={`${s.step} ${s[status]}`}
              aria-current={status === "active" ? "step" : undefined}
            >
              <span className={s.stepNumber} aria-hidden="true">
                {status === "completed" ? "✓" : `${index + 1}.`}
              </span>
              <span className={s.stepText}>{step.label}</span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
